import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PlusIcon,
  XMarkIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { adminService, type Role } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { PERMISSION_GROUPS } from '@lipaflow/shared'
import { apiErrorMessage } from '@lipaflow/shared'


type RoleCreateForm = {
  code: string
  display_name: string
  tenant_id: string
}

const emptyCreateForm: RoleCreateForm = {
  code: '',
  display_name: '',
  tenant_id: '',
}

export default function RolesPage() {
  usePageTitle('Roles & permissions')
  const queryClient = useQueryClient()
  const [includeInactive, setIncludeInactive] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [createForm, setCreateForm] = useState<RoleCreateForm>({ ...emptyCreateForm })
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editIsActive, setEditIsActive] = useState(true)
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [permissionSearch, setPermissionSearch] = useState('')

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles', includeInactive],
    queryFn: () => adminService.listRoles({ include_inactive: includeInactive }),
  })

  const createRoleMutation = useMutation({
    mutationFn: () =>
      adminService.createRole({
        code: createForm.code.trim(),
        display_name: createForm.display_name.trim(),
        tenant_id: createForm.tenant_id.trim(),
      }),
    onSuccess: (created) => {
      toast.success('Role created. Choose its permissions before assigning it.')
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      setShowCreateModal(false)
      setCreateForm({ ...emptyCreateForm })
      setEditingRole(created)
      setEditDisplayName(created.display_name)
      setEditIsActive(true)
      setSelectedPermissions([])
      setShowEditModal(true)
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Failed to create role'))
    },
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, displayName, isActive }: { roleId: string; displayName: string; isActive: boolean }) =>
      adminService.updateRole(roleId, {
        display_name: displayName,
        is_active: isActive,
      }),
  })

  const updatePermissionsMutation = useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: string; permissions: string[] }) =>
      adminService.setRolePermissions(roleId, permissions),
  })

  const roleRows = roles ?? []

  // Grouped by area with plain descriptions; codes a role already holds but
  // the catalogue doesn't know are kept (under "Other") so saving never drops them.
  const groupedPermissions = useMemo(() => {
    const q = permissionSearch.trim().toLowerCase()
    const known = new Set(PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.code)))
    const unknown = selectedPermissions.filter((p) => !known.has(p)).map((code) => ({ code, label: code }))
    const available = new Set(editingRole?.available_permissions ?? [])
    const offeredGroups = PERMISSION_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => available.has(item.code)),
    }))
    const groups = unknown.length ? [...offeredGroups, { area: 'Unsupported grants (remove before saving)', items: unknown }] : offeredGroups
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => !q || i.label.toLowerCase().includes(q) || i.code.includes(q) || g.area.toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0)
  }, [editingRole, permissionSearch, selectedPermissions])

  const handleCreate = () => {
    if (!createForm.code.trim() || !createForm.display_name.trim() || !createForm.tenant_id.trim()) {
      toast.error('Code, display name, and tenant ID are required')
      return
    }
    createRoleMutation.mutate()
  }

  const openEditModal = async (roleId: string) => {
    try {
      const role = await adminService.getRole(roleId)
      setEditingRole(role)
      setEditDisplayName(role.display_name)
      setEditIsActive(role.is_active)
      setSelectedPermissions(role.permissions || [])
      setPermissionSearch('')
      setShowEditModal(true)
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, 'Failed to load role details'))
    }
  }

  const togglePermission = (permission: string) => {
    setSelectedPermissions((current) =>
      current.includes(permission)
        ? current.filter((value) => value !== permission)
        : [...current, permission]
    )
  }

  const saveRoleChanges = async () => {
    if (!editingRole) return
    if (!editDisplayName.trim()) {
      toast.error('Display name is required')
      return
    }

    try {
      await updatePermissionsMutation.mutateAsync({
        roleId: editingRole.id,
        permissions: selectedPermissions,
      })

      await updateRoleMutation.mutateAsync({
        roleId: editingRole.id,
        displayName: editDisplayName.trim(),
        isActive: editIsActive,
      })

      toast.success('Role updated')
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      setShowEditModal(false)
      setEditingRole(null)
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, 'Failed to update role'))
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-h1 text-ink">Roles & Permissions</h1>
          <p className="font-inter text-body text-ink-muted mt-1">
            Create custom roles and assign granular permissions.
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <PlusIcon className="h-5 w-5" />
          Create Role
        </button>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-hairline bg-surface p-4">
        <p className="font-inter text-body-sm text-ink-muted">Include inactive roles</p>
        <button
          type="button"
          onClick={() => setIncludeInactive((value) => !value)}
          className={clsx(
            'inline-flex h-7 w-12 items-center rounded-full transition-colors',
            includeInactive ? 'bg-accent' : 'bg-hairline'
          )}
        >
          <span
            className={clsx(
              'h-5 w-5 rounded-full bg-surface transition-transform',
              includeInactive ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </button>
      </div>

      <div className="bg-surface rounded-2xl shadow-card border border-hairline overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-alt border-b border-hairline">
                <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Code</th>
                <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Display Name</th>
                <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Vertical</th>
                <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Permissions</th>
                <th className="px-6 py-4 text-right font-outfit text-caption text-ink-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : roleRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <ShieldCheckIcon className="h-12 w-12 text-ink-faint" />
                      <div>
                        <p className="font-outfit text-h4 text-ink">No roles found</p>
                        <p className="font-inter text-body-sm text-ink-muted mt-1">Create your first custom role.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                roleRows.map((role) => (
                  <tr key={role.id} className="hover:bg-teal-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-body-sm text-ink">{role.code}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-inter text-body-sm text-ink">{role.display_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-inter text-body-sm text-ink-muted">{role.vertical_code || 'Global'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx(
                        'inline-flex px-2.5 py-1 rounded-full text-caption font-medium',
                        role.is_system ? 'bg-info-100 text-info-700' : 'bg-teal-100 text-teal-700'
                      )}>
                        {role.is_system ? 'System' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx(
                        'inline-flex px-2.5 py-1 rounded-full text-caption font-medium',
                        role.is_active ? 'bg-success-100 text-success-700' : 'bg-surface-alt text-ink-muted'
                      )}>
                        {role.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-inter text-body-sm text-ink">{role.permissions.length}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openEditModal(role.id)}
                        disabled={role.is_system}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-body-sm font-inter text-teal-700 hover:bg-teal-50 transition-colors"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                        {role.is_system ? 'Code managed' : 'Manage'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50 px-4">
          <div className="w-full max-w-xl rounded-2xl bg-surface shadow-2xl border border-hairline">
            <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
              <h2 className="font-outfit text-h3 text-ink">Create Role</h2>
              <button type="button" onClick={() => setShowCreateModal(false)} className="p-2 rounded-lg hover:bg-surface-alt">
                <XMarkIcon className="h-5 w-5 text-ink-muted" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="label">Role Code</label>
                <input
                  value={createForm.code}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      code: event.target.value.trim().toLowerCase().replace(/\s+/g, '_'),
                    }))
                  }
                  className="input"
                  placeholder="e.g. collections_manager"
                />
              </div>

              <div>
                <label className="label">Display Name</label>
                <input
                  value={createForm.display_name}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, display_name: event.target.value }))}
                  className="input"
                  placeholder="e.g. Collections Manager"
                />
              </div>

              <div>
                <label className="label">Tenant ID</label>
                <input
                  value={createForm.tenant_id}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, tenant_id: event.target.value }))}
                  className="input"
                  placeholder="Organization UUID"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-hairline">
              <button type="button" onClick={() => setShowCreateModal(false)} className="btn-ghost">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
                disabled={createRoleMutation.isPending}
              >
                {createRoleMutation.isPending ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50 px-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-surface shadow-2xl border border-hairline">
            <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
              <div>
                <h2 className="font-outfit text-h3 text-ink">Manage Role</h2>
                <p className="font-inter text-caption text-ink-muted mt-1">{editingRole.code}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false)
                  setEditingRole(null)
                }}
                className="p-2 rounded-lg hover:bg-surface-alt"
              >
                <XMarkIcon className="h-5 w-5 text-ink-muted" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Display Name</label>
                  <input
                    value={editDisplayName}
                    onChange={(event) => setEditDisplayName(event.target.value)}
                    className="input"
                  />
                </div>
                <label className="inline-flex items-center gap-2 font-inter text-body-sm text-ink pt-8">
                  <input
                    type="checkbox"
                    checked={editIsActive}
                    onChange={(event) => setEditIsActive(event.target.checked)}
                  />
                  Role is active
                </label>
              </div>

              <div>
                <label className="label">Search permissions</label>
                <input
                  value={permissionSearch}
                  onChange={(event) => setPermissionSearch(event.target.value)}
                  className="input"
                  placeholder="e.g. payouts, attendance, export"
                />
              </div>

              <div className="space-y-4 max-h-[420px] overflow-y-auto border border-hairline rounded-xl p-4">
                {groupedPermissions.map((group) => (
                  <fieldset key={group.area}>
                    <legend className="font-outfit text-h5 text-ink mb-2">{group.area}</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {group.items.map((item) => (
                        <label key={item.code} className="flex items-start gap-2 text-body-sm text-ink">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedPermissions.includes(item.code)}
                            onChange={() => togglePermission(item.code)}
                          />
                          <span>
                            <span className="block">{item.label}</span>
                            <span className="block font-mono text-caption text-ink-faint">{item.code}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-hairline">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false)
                  setEditingRole(null)
                }}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRoleChanges}
                className="btn-primary"
                disabled={updateRoleMutation.isPending || updatePermissionsMutation.isPending}
              >
                {updateRoleMutation.isPending || updatePermissionsMutation.isPending ? 'Saving...' : 'Save Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
